import { MenuHeader, MenuSeparator } from "react-ui";
import * as React from "react";

import { isNotNullOrEmpty, isNotNullOrUndefined, isNullOrEmpty } from "../../../../../Common/TypingUtils";
import { IterUtils } from "../../../../../Common/IterableUtils";
import {
    PicklistDataItem,
    PicklistQueryResponse,
    unionWithOrFilters,
} from "../../../Engine/Core/Picklists/PicklistTypes";
import {
    DigestFilterOperator,
    DigestLogicalOperator,
    DigestQueryFilter,
} from "../../../../../Common/CandyApiClient/DigestApiClient/Types/DigestQueryFilter";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";

import { ComboBoxProps } from "./ComboBox";
import { MostLikelyCaption } from "./ComboBox.styles";
import { buildAutoValueFilter, buildEmptyValueFilter, buildSearchFilter } from "./ComboboxFilterBuilder";
import { defaultLimit, makeRequest } from "./makeRequest";
import { getColumnNames } from "./getColumnNames";

export interface ComboBoxDataItem {
    picklistDataItem: PicklistDataItem;
    highlightedLabel?: Partial<PicklistDataItem>;
    isMostLikelyItem?: boolean;
    isAutoValueItem?: boolean;
}

export function isComboBoxDataItem(obj: {}): obj is ComboBoxDataItem {
    return typeof obj === "object" && "picklistDataItem" in obj;
}

interface IComboBoxSearch {
    pattern: string;
    patternLowerCase: string;
}

interface ItemType {
    items: Array<ComboBoxDataItem | JSX.Element>;
    itemsCount: number;
}

export interface ComboboxDependency {
    path: AbsoluteResolvedModelPath;
    field: string;
    value: string;
}

export const getComboBoxItems = async (props: {
    pattern: string;
    comboboxProps: ComboBoxProps;
}): Promise<{
    itemsInfo: ItemType;
    commonItemsInfo: { commonItems: ComboBoxDataItem[]; commonItemsSuitableCount: number; search: IComboBoxSearch };
}> => {
    const { pattern, comboboxProps } = props;
    const search: IComboBoxSearch = { pattern: pattern, patternLowerCase: pattern.toLowerCase().trim() };

    const { enumerations } = comboboxProps;
    if (isNotNullOrUndefined(enumerations) && enumerations.length > 0) {
        let items;
        if (pattern) {
            const filteredEnumerations = filterItemsBySearch(enumerations, search);
            items = filteredEnumerations.map(item => convertToComboBoxItem(item, search, comboboxProps));
        } else {
            items = enumerations.map(item => convertToComboBoxItem(item, search, comboboxProps));
        }

        if (comboboxProps.filterCallback != undefined) {
            items = (await comboboxProps.filterCallback(items)) as ComboBoxDataItem[];
        }

        return {
            itemsInfo: { items: sortItems(items, props.comboboxProps), itemsCount: 0 },
            commonItemsInfo: { commonItems: [], commonItemsSuitableCount: 0, search: search },
        };
    }

    const { mostLikelyItems } = await getMostLikelyItems(search, comboboxProps);
    let { commonItems, commonItemsSuitableCount } = await getCommonItems(search, comboboxProps);
    let { items, itemsCount } = await joinItems(search, mostLikelyItems, commonItems, comboboxProps);

    if (comboboxProps.filterCallback != undefined) {
        commonItems = (await comboboxProps.filterCallback(commonItems)) as ComboBoxDataItem[];
        commonItemsSuitableCount = commonItems.length;

        items = await comboboxProps.filterCallback(items);
        itemsCount = items.length;
    }

    return {
        itemsInfo: { items: items, itemsCount: itemsCount },
        commonItemsInfo: {
            commonItems: commonItems,
            commonItemsSuitableCount: commonItemsSuitableCount,
            search: search,
        },
    };
};

export const getPicklistItem = async (
    value: string | number,
    comboboxProps: ComboBoxProps
): Promise<undefined | PicklistDataItem> => {
    const { onQueryPicklist, id } = comboboxProps;
    let { keyColumnName } = getColumnNames(comboboxProps);

    if (comboboxProps.valueToSaveColumnName) {
        keyColumnName = comboboxProps.valueToSaveColumnName;
    }

    const filterQuery: DigestQueryFilter = {
        filter: {
            operator: DigestLogicalOperator.Or,
            operands: [{ operator: DigestFilterOperator.Equal, column: `${keyColumnName}`, value: value?.toString() }],
        },
    };

    let { items } = await onQueryPicklist(filterQuery);
    if (comboboxProps.filterCallback != undefined) {
        const filteredComboboxDataItems = (await comboboxProps.filterCallback(
            items.map(it => ({
                picklistDataItem: it,
            }))
        )) as ComboBoxDataItem[];
        items = filteredComboboxDataItems.map(it => it.picklistDataItem);
    }
    // Поиск по id является уточняющим и идет вторым приоритетом
    const itemFilteredById = id ? items.find(picklistItem => picklistItem.id === id) || items[0] : items[0];
    return itemFilteredById;
};

const isEquals = (actual: ComboBoxDataItem, expected: ComboBoxDataItem, keyColumnName: string): boolean =>
    actual.picklistDataItem[keyColumnName] === expected.picklistDataItem[keyColumnName];

const sortItems = (items: ComboBoxDataItem[], comboboxProps: ComboBoxProps): ComboBoxDataItem[] =>
    isNotNullOrEmpty(comboboxProps.autoValue)
        ? IterUtils.sortBy(items, item => (item.isAutoValueItem ? "0" : "1"))
        : items;

const sortBySelectedThenByAuto = (
    items: ComboBoxDataItem[],
    isSelected: (i: ComboBoxDataItem) => boolean,
    comboboxProps: ComboBoxProps
): ComboBoxDataItem[] =>
    isNotNullOrEmpty(comboboxProps.autoValue)
        ? IterUtils.sortBy(items, item => (isSelected(item) ? "-1" : item.isAutoValueItem ? "0" : "1"))
        : items;

const joinItems = async (
    search: IComboBoxSearch,
    mostLikelyItems: ComboBoxDataItem[],
    commonItems: ComboBoxDataItem[],
    comboboxProps: ComboBoxProps
): Promise<ItemType> => {
    const { value } = comboboxProps;
    const { keyColumnName } = getColumnNames(comboboxProps);

    if (isNotNullOrUndefined(value) && isNotNullOrEmpty(value?.toString())) {
        const item = await getPicklistItem(value, comboboxProps);
        if (
            item != undefined &&
            (isNullOrEmpty(search.pattern) || (item[keyColumnName]?.toString() ?? "").includes(search.patternLowerCase))
        ) {
            const comboBoxDataItem = convertToComboBoxItem(item, search, comboboxProps);
            return joinWithSelectedItem(commonItems, comboBoxDataItem, comboboxProps);
        }
    }

    return joinItemsWithPopular(mostLikelyItems, commonItems, comboboxProps);
};

const joinWithSelectedItem = (
    commonItems: ComboBoxDataItem[],
    item: ComboBoxDataItem,
    comboboxProps: ComboBoxProps
): { items: Array<ComboBoxDataItem | JSX.Element>; itemsCount: number } => {
    const { keyColumnName } = getColumnNames(comboboxProps);
    const limit = comboboxProps.limit ?? defaultLimit;

    const itemIsVisible = commonItems.find(i => isEquals(i, item, keyColumnName));
    if (!itemIsVisible) {
        const sortedCommonItems = sortItems(commonItems, comboboxProps);
        return {
            items: [item, ...sortedCommonItems].slice(0, limit),
            itemsCount: Math.min(commonItems.length + 1, limit),
        };
    } else {
        const sortedCommonItems = sortBySelectedThenByAuto(
            commonItems,
            i => isEquals(i, item, keyColumnName),
            comboboxProps
        );
        return { items: sortedCommonItems, itemsCount: commonItems.length };
    }
};

const joinItemsWithPopular = (
    mostLikelyItems: ComboBoxDataItem[],
    commonItems: ComboBoxDataItem[],
    comboboxProps: ComboBoxProps
): { items: Array<ComboBoxDataItem | JSX.Element>; itemsCount: number } => {
    const limit = comboboxProps.limit ?? defaultLimit;
    const sortedCommonItems = sortItems(commonItems, comboboxProps);
    if (mostLikelyItems.length === 0) {
        return { items: sortedCommonItems, itemsCount: commonItems.length };
    }

    const sortedMostLikelyItems = sortItems(mostLikelyItems, comboboxProps);
    const mostLikelyCaption = renderMostLikelyCaption(comboboxProps.mostLikelyCaption);
    if (limit <= mostLikelyItems.length) {
        return {
            items: [mostLikelyCaption, ...sortedMostLikelyItems],
            itemsCount: mostLikelyItems.length,
        };
    } else {
        return {
            items: [
                mostLikelyCaption,
                ...sortedMostLikelyItems,
                <MenuSeparator key="separator" />,
                ...excludeAutoValueItems(sortedCommonItems, comboboxProps),
            ].slice(0, limit + 2),
            itemsCount: limit,
        };
    }
};

const excludeAutoValueItems = (items: ComboBoxDataItem[], comboboxProps: ComboBoxProps): ComboBoxDataItem[] =>
    isNotNullOrEmpty(comboboxProps.autoValue) ? items.filter(item => !item.isAutoValueItem) : items;

const getMostLikelyItems = async (
    search: IComboBoxSearch,
    comboboxProps: ComboBoxProps
): Promise<{ mostLikelyItems: ComboBoxDataItem[]; mostLikelyItemsSuitableCount: number }> => {
    const { mostLikelyFilter } = comboboxProps;

    let responseMostLikelyItems: PicklistQueryResponse | undefined = {
        items: [],
        suitableCount: 0,
        columnMetas: [],
    };
    if (isNullOrEmpty(search.pattern) && isNotNullOrUndefined(mostLikelyFilter)) {
        responseMostLikelyItems = await makeRequest(
            buildAutoValueFilter(comboboxProps),
            comboboxProps,
            mostLikelyFilter,
            DigestLogicalOperator.Or
        );
        return {
            mostLikelyItems: responseMostLikelyItems.items.map(item =>
                convertToComboBoxItem(item, search, comboboxProps, true)
            ),
            mostLikelyItemsSuitableCount: responseMostLikelyItems.suitableCount,
        };
    }
    return { mostLikelyItems: [], mostLikelyItemsSuitableCount: 0 };
};

const getCommonItems = async (
    search: IComboBoxSearch,
    comboboxProps: ComboBoxProps
): Promise<{ commonItems: ComboBoxDataItem[]; commonItemsSuitableCount: number }> => {
    const { filter } = comboboxProps;

    let response: PicklistQueryResponse | undefined;

    if (isNullOrEmpty(search.pattern)) {
        response = await makeRequest(buildEmptyValueFilter(), comboboxProps, filter);
    }

    if (!response || response.suitableCount === 0) {
        response = await makeRequest(
            unionWithOrFilters(
                buildSearchFilter(search.patternLowerCase, comboboxProps),
                buildAutoValueFilter(comboboxProps)
            ),
            comboboxProps,
            filter
        );
    }

    return addAutoValueItems(response, search, comboboxProps);
};

//NOTE: единственный простой фильтр по пустому значению (типа code='') возвращает все записи, поэтому его нельзя просто добавить к автофильтру через Or,
// в этом случае будут фильтроваться на пустое значение и останутся только автозначения, почему такое поведение в picklists и elastic - непонятно
const addAutoValueItems = async (
    response: PicklistQueryResponse,
    search: IComboBoxSearch,
    comboboxProps: ComboBoxProps
): Promise<{ commonItems: ComboBoxDataItem[]; commonItemsSuitableCount: number }> => {
    const { autoValue } = comboboxProps;

    const items = response.items.map(item => convertToComboBoxItem(item, search, comboboxProps));
    if (isNullOrEmpty(autoValue) || hasAutoValueItems(items)) {
        return { commonItems: items, commonItemsSuitableCount: response.suitableCount };
    }
    const autoValuesResponse = await makeRequest(buildAutoValueFilter(comboboxProps), comboboxProps);
    const autoValueItems = autoValuesResponse.items.map(item => convertToComboBoxItem(item, search, comboboxProps));
    const limit = comboboxProps.limit ?? defaultLimit;

    return {
        commonItems: [...autoValueItems, ...items].slice(0, limit),
        commonItemsSuitableCount: response.suitableCount,
    };
};

const hasAutoValueItems = (items: ComboBoxDataItem[]): boolean => items.some(item => item.isAutoValueItem);

const convertToComboBoxItem = (
    picklistDataItem: PicklistDataItem,
    search: IComboBoxSearch,
    comboboxProps: ComboBoxProps,
    isMostLikelyItem: boolean = false
): ComboBoxDataItem => {
    const { keyColumnName, valueColumnName } = getColumnNames(comboboxProps);

    const key = (picklistDataItem[keyColumnName]?.toString() ?? "").toLocaleLowerCase();
    const value = (picklistDataItem[valueColumnName]?.toString() ?? "").toLocaleLowerCase();

    const autoValue = comboboxProps.autoValue?.toLocaleLowerCase();
    const isAutoValueItem = autoValue === key;

    return {
        picklistDataItem: picklistDataItem,
        highlightedLabel: {
            [keyColumnName]: key.includes(search.patternLowerCase) ? search.pattern : undefined,
            [valueColumnName]: value.includes(search.patternLowerCase) ? search.pattern : undefined,
        },
        isMostLikelyItem: isMostLikelyItem,
        isAutoValueItem: isAutoValueItem,
    };
};

const renderMostLikelyCaption = (mostLikelyCaption?: string): JSX.Element => (
    <MenuHeader>
        <MostLikelyCaption data-tid="combobox-most-likely-caption">{mostLikelyCaption}</MostLikelyCaption>
    </MenuHeader>
);

const filterItemsBySearch = (enumerations: PicklistDataItem[], search: IComboBoxSearch): PicklistDataItem[] => {
    const comboboxItems: PicklistDataItem[] = [];
    const lowerCaseSearchPattern = search.pattern.toLowerCase();

    enumerations.forEach(item => {
        const inName = item.name?.toString().toLowerCase().includes(lowerCaseSearchPattern);
        const inCode = item.code?.toString().toLowerCase().includes(lowerCaseSearchPattern);

        if (inCode || inName) {
            comboboxItems.push(item);
        }
    });
    return comboboxItems;
};
